import { getPriceHistory, companyDevelopments } from '@simplywallst/services';
import type { QueryFunctionContext } from '@tanstack/react-query';
import inversePriceHistory from '@components/PriceChart/utils/inversePriceHistory';

export function getQueryKey(
  lookup: string,
  timestamp = 0
): [string, string, number] {
  return ['price-history', lookup, timestamp];
}
export const priceQueryFn = async (
  context: QueryFunctionContext<[string, string | undefined, number]>
) => {
  const { 1: lookup, 2: timestamp } = context.queryKey;
  if (typeof lookup !== 'undefined') {
    const result = await getPriceHistory({
      companyId: lookup,
      payload: {
        start_timestamp: timestamp,
      },
    });
    const sorted = result.data.data.sort((a, b) => a.date - b.date);
    return {
      prices: sorted,
      inversePrices: inversePriceHistory(sorted),
    };
  }
};

export type Category =
  | 'dividend'
  | 'financial'
  | 'management'
  | 'strategy'
  | 'other';

function coerceCategory(category: string | null = '') {
  const safeCategory =
    typeof category === 'string' ? category.toLowerCase() : '';
  if (
    safeCategory === 'dividend' ||
    safeCategory === 'financial' ||
    safeCategory === 'management' ||
    safeCategory === 'strategy' ||
    safeCategory === 'other'
  ) {
    return safeCategory;
  }
  return 'other';
}
export const eventsQueryFn = async (
  context: QueryFunctionContext<
    [string, string | undefined, number | undefined, Category[]]
  >
) => {
  const { 1: lookup, 2: timestamp, 3: categoriesParam } = context.queryKey;
  if (typeof lookup === 'undefined' || typeof timestamp === 'undefined') return;
  const response = await companyDevelopments({
    companyId: lookup,
    categories: categoriesParam,
    payload: {
      start_timestamp: timestamp,
      limit: 1000,
    },
  });
  const timeSeries = response.data.data;
  const eventHistory = timeSeries.map((series) => {
    const eventHistoryItem: {
      date: number;
      events: { id: string; category: Category; name: string }[];
    } = {
      date: series.date,
      events: [],
    };
    series.events.data.forEach((event) => {
      const safeId = event.id.toString();
      event.types.data.forEach((type) => {
        eventHistoryItem.events.push({
          id: safeId,
          category: coerceCategory(type.group),
          name: type.shortName,
        });
      });
    });
    return eventHistoryItem;
  });
  return {
    eventHistory,
  };
};
